import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import api from '../../services/api'; // ✅ Corrected: Import your centralized api instance
import './Admin.css';
import Sliders from './Sliders';
import Officials from './Officials';
import PressReleases from './PressReleases';
import Testimonials from './Testimonials';
import Partners from './Partners';
import Courses from './Courses';
import Campuses from './Campuses';
import Messages from './Messages';

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [stats, setStats] = useState({
        sliders: 0,
        officials: 0,
        pressReleases: 0,
        testimonials: 0,
        partners: 0,
        courses: 0,
        messages: 0,
    });
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('admin_token');
        if (!token) {
            navigate('/admin/login');
            return;
        }

        // ✅ Get user info using relative paths via the 'api' service
        api.get('/admin/user')
            .then(response => {
                setUser(response.data);
            })
            .catch((error) => {
                console.error('Error fetching user:', error);
                // The interceptor in api.js handles 401s, but we keep this as a fallback
                if (error.response?.status === 401) {
                    localStorage.removeItem('admin_token');
                    localStorage.removeItem('admin_user');
                    navigate('/admin/login');
                }
                const storedUser = localStorage.getItem('admin_user');
                if (storedUser) {
                    setUser(JSON.parse(storedUser));
                }
            });

        // ✅ Get stats using relative paths
        // These requests go to https://pitac.gov.pk/api/admin/... automatically
        Promise.all([
            api.get('/admin/sliders'),
            api.get('/admin/officials'),
            api.get('/admin/press-releases'),
            api.get('/admin/testimonials'),
            api.get('/admin/partners'),
            api.get('/admin/courses'),
            api.get('/admin/contact-messages'),
        ]).then(([sliders, officials, press, testimonials, partners, courses, messages]) => {
            setStats({
                sliders: sliders.data.length || 0,
                officials: officials.data.length || 0,
                pressReleases: press.data.length || 0,
                testimonials: testimonials.data.length || 0,
                partners: partners.data.length || 0,
                courses: courses.data.length || 0,
                messages: (messages.data || []).filter(m => !m.is_read).length || 0,
            });
        }).catch(error => {
            console.error('Error fetching stats:', error);
        });
    }, [navigate]);

    const handleLogout = () => {
        // ✅ Use 'api' instance for logout
        api.post('/admin/logout')
            .catch(console.error)
            .finally(() => {
                localStorage.removeItem('admin_token');
                localStorage.removeItem('admin_user');
                navigate('/admin/login');
            });
    };

    const menuItems = [
        { path: '/admin', icon: '📊', label: 'Dashboard' },
        { path: '/admin/sliders', icon: '🖼️', label: 'Sliders' },
        { path: '/admin/officials', icon: '👤', label: 'Officials' },
        { path: '/admin/press-releases', icon: '📰', label: 'Press Releases' },
        { path: '/admin/testimonials', icon: '💬', label: 'Testimonials' },
        { path: '/admin/partners', icon: '🤝', label: 'Partners' },
        { path: '/admin/courses', icon: '📚', label: 'Courses' },
        { path: '/admin/campuses', icon: '🏫', label: 'Campuses' },
        { path: '/admin/messages', icon: '✉️', label: 'Messages' },
    ];

    return (
        <div className="admin-dashboard">
            <aside className="admin-sidebar">
                <div className="admin-sidebar-logo">
                    <img src="/images/pitac-logo1.png" alt="PITAC" />
                    <h3>PITAC</h3>
                    <small>Admin Panel</small>
                </div>
                <ul className="admin-sidebar-menu">
                    {menuItems.map(item => (
                        <li key={item.path}>
                            <Link to={item.path} className={window.location.pathname === item.path ? 'active' : ''}>
                                <span className="icon">{item.icon}</span>
                                <span>{item.label}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
            </aside>

            <main className="admin-main">
                <header className="admin-topbar">
                    <h1>{menuItems.find(m => m.path === window.location.pathname)?.label || 'Dashboard'}</h1>
                    <div className="admin-topbar-actions">
                        <div className="admin-user">
                            <div className="admin-user-avatar">
                                {user?.name?.charAt(0) || 'A'}
                            </div>
                            <span>{user?.name || 'Admin'}</span>
                        </div>
                        <button className="admin-logout-btn" onClick={handleLogout}>
                            Logout
                        </button>
                    </div>
                </header>

                <div className="admin-content">
                    <Routes>
                        <Route path="/" element={
                            <>
                                <div className="admin-stats-grid">
                                    <div className="admin-stat-card">
                                        <h3>Sliders</h3>
                                        <div className="stat-number">{stats.sliders}</div>
                                    </div>
                                    <div className="admin-stat-card">
                                        <h3>Officials</h3>
                                        <div className="stat-number">{stats.officials}</div>
                                    </div>
                                    <div className="admin-stat-card">
                                        <h3>Press Releases</h3>
                                        <div className="stat-number">{stats.pressReleases}</div>
                                    </div>
                                    <div className="admin-stat-card">
                                        <h3>Testimonials</h3>
                                        <div className="stat-number">{stats.testimonials}</div>
                                    </div>
                                    <div className="admin-stat-card">
                                        <h3>Partners</h3>
                                        <div className="stat-number">{stats.partners}</div>
                                    </div>
                                    <div className="admin-stat-card">
                                        <h3>Courses</h3>
                                        <div className="stat-number">{stats.courses}</div>
                                    </div>
                                    <div className="admin-stat-card">
                                        <h3>Unread Messages</h3>
                                        <div className="stat-number">{stats.messages}</div>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '2.5rem', color: '#6b7a8f' }}>
                                    <h2 style={{ color: '#0f2b4f', fontWeight: '600', marginBottom: '0.5rem' }}>Welcome to the PITAC Admin Panel</h2>
                                    <p style={{ fontSize: '0.95rem' }}>Select a section from the sidebar to manage content</p>
                                </div>
                            </>
                        } />
                        <Route path="sliders" element={<Sliders />} />
                        <Route path="officials" element={<Officials />} />
                        <Route path="press-releases" element={<PressReleases />} />
                        <Route path="testimonials" element={<Testimonials />} />
                        <Route path="partners" element={<Partners />} />
                        <Route path="courses" element={<Courses />} />
                        <Route path="campuses" element={<Campuses />} />
                        <Route path="messages" element={<Messages />} />
                    </Routes>
                </div>
            </main>
        </div>
    );
};

export default Dashboard;